import esriConfig from "@arcgis/core/config";
import { runtimeConfig } from "@src/runtime-config";
import "@arcgis/map-components/components/arcgis-map";
import { useMapInitialization } from "@components/world-map/hooks/use-map-initialization";
import { useMapViewSync } from "@components/world-map/hooks/use-map-view-sync";
import { useVisibleEntities } from "@components/world-map/hooks/use-visible-entities";
import { useWorldMapHighlight } from "@components/world-map/hooks/use-world-map-highlight";
import { useWorldMapPopups } from "@components/world-map/hooks/use-world-map-popups";
import { WorldMapControls } from "@components/world-map/world-map-controls";
import {
  WORLD_MAP_DEFAULT_FIELDS,
  WORLD_MAP_FOCUS_ZOOM,
} from "@components/world-map/world-map-constants";
import {
  WorldMapEntity,
  WorldMapProps,
} from "@components/world-map/world-map-types";
import {
  getHorizontalTranslate,
  getVerticalTranslate,
} from "@components/world-map/world-map-utils";
import { Box } from "@mui/material";
import { useImperativeHandle } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { worldMapStyleSelector } from "@store/ui/ui-selector";
import { setWorldMapStyle } from "@store/ui/ui-slice";

// Authenticate with ArcGIS services using the API key on app initialization.
// This ensures that all imports from @arcgis/core will include the key.
esriConfig.apiKey = runtimeConfig.features.arcgisApiKey;

/**
 * World map that can display entities on a geographical map.
 */
export function WorldMap<T extends WorldMapEntity>({
  entities,
  onVisibleEntitiesChange,
  fields = WORLD_MAP_DEFAULT_FIELDS,
  buttons,
  shouldShowMapControls = false,
  sx,
  mapRef,
  renderPopupContent,
  onClusterClick,
  onEntityHover,
  onClusterHover,
  onMapClick,
  initialView,
  onViewStable,
}: WorldMapProps<T>): JSX.Element {
  const dispatch = useAppDispatch();
  const mapStyle = useAppSelector(worldMapStyleSelector);
  const shouldEnablePopups = Boolean(renderPopupContent);

  const { viewRef, layerRef, isViewReady, handleViewReady } =
    useMapInitialization({ entities, fields, mapStyle, initialView });

  useMapViewSync(viewRef, isViewReady, onViewStable);

  const { selectedMarkerPopup } = useWorldMapPopups({
    viewRef,
    layerRef,
    isViewReady,
    entities,
    isPopupEnabled: shouldEnablePopups,
    onClusterClick,
    onMapClick,
  });

  const { viewAllEntities } = useVisibleEntities(
    viewRef,
    isViewReady,
    entities,
    onVisibleEntitiesChange
  );

  const { highlightEntity } = useWorldMapHighlight(
    viewRef,
    layerRef,
    isViewReady,
    mapStyle,
    onEntityHover,
    selectedMarkerPopup?.entity.id,
    onClusterHover
  );

  useImperativeHandle(
    mapRef,
    () => ({
      goTo: (position, targetZoom) => {
        void viewRef.current?.goTo({
          center: [position.long, position.lat],
          zoom: targetZoom ?? WORLD_MAP_FOCUS_ZOOM,
        });
      },
      highlightEntity,
    }),
    [viewRef, highlightEntity]
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        ...sx,
      }}
    >
      <arcgis-map
        data-testid="WORLD_MAP"
        basemap="gray-vector"
        onarcgisViewReadyChange={handleViewReady}
      />
      {shouldEnablePopups && selectedMarkerPopup && renderPopupContent && (
        <Box
          sx={{
            position: "absolute",
            left: selectedMarkerPopup.x,
            top: selectedMarkerPopup.y,
            transform: `translate(${getHorizontalTranslate(
              selectedMarkerPopup.horizontalPlacement,
              selectedMarkerPopup.placement
            )}, ${getVerticalTranslate(selectedMarkerPopup.placement)})`,
            zIndex: 2,
          }}
        >
          {renderPopupContent(selectedMarkerPopup.entity)}
        </Box>
      )}
      {isViewReady && (
        <WorldMapControls
          viewRef={viewRef}
          shouldShowMapControls={shouldShowMapControls}
          viewAllEntities={viewAllEntities}
          mapStyle={mapStyle}
          setMapStyle={(mapStyle) => {
            dispatch(setWorldMapStyle(mapStyle));
          }}
          externalButtons={buttons}
        />
      )}
    </Box>
  );
}
